(function(root) {
  "use strict";

  const namespace = root.LegitGrails = root.LegitGrails || {};
  const consent = namespace.cookieConsent;
  const policy = consent.policy;
  const storage = consent.storage;
  const version = consent.config.policyVersion;
  let subscribers = [];
  let revision = 0;
  let state = {
    region: "EU",
    version: version,
    source: "DEFAULT",
    regionStatus: "pending",
    categories: policy.cloneCategories(policy.rejectAll),
    saleOfData: null,
    hasExplicitConsent: false,
    needsSync: false,
    isRegionResolved: false,
    updatedAt: null
  };

  /** @param {object} candidate Internal state or a previous snapshot. @returns {ConsentState} */
  function snapshot(candidate) {
    return Object.assign({}, candidate, {
      controlEnabled: true,
      decisionRevision: candidate.decisionRevision === undefined ? revision : candidate.decisionRevision,
      categories: policy.cloneCategories(candidate.categories),
      effectiveCategories: policy.effectiveCategories(candidate)
    });
  }

  function getState() {
    root.legitGrailsCookieConsent = snapshot(state);
    return snapshot(state);
  }

  function warn(error) {
    if (root.console && root.console.warn) root.console.warn("[cookie-consent] Subscriber failed", error);
  }

  /** Publishes one atomic transition. Subscriber failure cannot block other consumers. */
  function setState(patch, options) {
    const previous = snapshot(state);
    if (options.changeType !== "shopify-sync") revision += 1;
    state = Object.assign({}, state, patch);
    if (options.persist) storage.write(state);
    const current = getState();
    const detail = {
      state: current,
      previousState: previous,
      effectiveCategories: current.effectiveCategories,
      previousEffectiveCategories: previous.effectiveCategories,
      changeType: options.changeType,
      downgradedCategories: policy.optionalKeys.filter(function(key) {
        return previous.effectiveCategories[key] && !current.effectiveCategories[key];
      })
    };
    subscribers.slice().forEach(function(listener) {
      try { listener(snapshot(state), detail); } catch (error) { warn(error); }
    });
    try {
      document.dispatchEvent(new CustomEvent("legitgrails:cookie-consent-changed", { detail: detail }));
    } catch (error) {
      warn(error);
    }
  }

  function compatibleChoice(stored, region, confirmed) {
    if (!stored || !stored.meta.hasExplicitConsent || stored.consent.region === "ROW" || region === "ROW") return null;
    const sameRegion = stored.consent.region === region;
    const chosenDuringFallback = stored.consent.regionStatus === "fallback";
    return sameRegion || chosenDuringFallback || !confirmed ? stored : null;
  }

  /**
   * Applies confirmed geography or an error-only EU fallback. Technical fallback
   * does not overwrite storage. Explicit fallback choices survive EU/CA recovery;
   * confirmed ROW always replaces any choice with regional Accept all.
   * @param {ConsentRegionContext} context
   * @param {{persist?: boolean, changeType?: string}} [options]
   */
  function hydrateRegion(context, options) {
    const confirmed = Boolean(context && context.source === "SHOPIFY" && policy.isRegion(context.region));
    const region = confirmed ? context.region : "EU";
    const status = confirmed ? "confirmed" : "fallback";
    const choice = compatibleChoice(storage.read(), region, confirmed);
    const movedChoice = choice && (choice.consent.region !== region || choice.consent.regionStatus !== status);
    const categories = choice
      ? policy.cloneCategories(choice.consent.categories)
      : policy.defaultCategories(region);

    // A legacy contradictory record must not turn its native opt-out into allow,
    // including while technical fallback temporarily uses the EU UI.
    if (choice && choice.consent.saleOfData === false) {
      categories.analytics = false;
      categories.advertisement = false;
    }
    let sale = choice && region === "USA_CA" ? choice.consent.saleOfData : null;
    if (region === "USA_CA" && choice && sale === null) {
      sale = categories.analytics || categories.advertisement;
    }

    setState({
      region: region,
      version: version,
      regionStatus: status,
      source: choice ? choice.consent.source : confirmed ? "SHOPIFY" : "CONSERVATIVE_FALLBACK",
      categories: categories,
      saleOfData: sale,
      hasExplicitConsent: Boolean(choice),
      needsSync: Boolean(choice && (choice.meta.needsSync || (confirmed && movedChoice))),
      isRegionResolved: true,
      updatedAt: choice ? choice.consent.updatedAt : null
    }, {
      persist: confirmed && !(options && options.persist === false),
      changeType: options && options.changeType || "hydrate"
    });
  }

  /** A cached heap must not replace a newer/missing/corrupt cookie with ROW opt-in. */
  function restoreFromStorage() {
    if (!state.isRegionResolved) return getState();
    const stored = storage.read();
    const compatible = stored && stored.consent.region === state.region &&
      stored.consent.version === state.version && state.regionStatus === "confirmed";
    if (!compatible) {
      setState({
        regionStatus: "pending",
        categories: policy.cloneCategories(policy.rejectAll),
        saleOfData: null,
        hasExplicitConsent: false,
        needsSync: false,
        isRegionResolved: false,
        updatedAt: null
      }, { persist: false, changeType: "restore" });
    } else {
      hydrateRegion({ region: state.region, source: "SHOPIFY", version: version }, {
        persist: false, changeType: "restore"
      });
    }
    return getState();
  }

  /**
   * Persists a visitor choice; never accepts truthy values as booleans.
   * saleOfData is independent of the exact Do Not Sell preset. Only an explicit
   * analytics/advertisement grant releases a previous native opt-out (except GPC).
   * @param {Partial<ConsentCategories>} categories
   * @param {{saleOfData?: boolean|null}} [options]
   */
  function setConsent(categories, options) {
    if (!state.isRegionResolved || (state.region === "ROW" && state.regionStatus === "confirmed")) return;
    const next = policy.cloneCategories(Object.assign({}, state.categories, categories || {}));
    let sale = state.region === "USA_CA" ? state.saleOfData : null;
    if (state.region === "USA_CA") {
      if (options && typeof options.saleOfData === "boolean") {
        sale = options.saleOfData;
      } else if (!policy.hasGpc() && categories && (categories.analytics === true || categories.advertisement === true)) {
        sale = true;
      }
      if (sale === false || policy.hasGpc()) {
        next.analytics = false;
        next.advertisement = false;
      }
    }

    const unchanged = policy.sameCategories(state.categories, next) && state.saleOfData === sale;
    if (unchanged && state.hasExplicitConsent && state.needsSync) return;
    setState({
      source: "LOCAL",
      categories: next,
      saleOfData: sale,
      hasExplicitConsent: true,
      needsSync: true,
      updatedAt: new Date().toISOString()
    }, { persist: true, changeType: "user" });
  }

  function subscribe(listener) {
    if (typeof listener !== "function") return function() {};
    subscribers.push(listener);
    return function() {
      subscribers = subscribers.filter(function(candidate) { return candidate !== listener; });
    };
  }

  Object.assign(consent, {
    getState: getState,
    getEffectiveCategories: function() { return policy.effectiveCategories(state); },
    hydrateRegion: hydrateRegion,
    restoreFromStorage: restoreFromStorage,
    setConsent: setConsent,
    acceptAll: function(options) { setConsent(policy.acceptAll, options); },
    rejectAll: function(options) { setConsent(policy.rejectAll, options); },
    applyDoNotSellPreset: function() { setConsent(policy.doNotSellPreset, { saleOfData: false }); },
    markShopifySynced: function(expectedRevision) {
      if ((expectedRevision !== undefined && expectedRevision !== revision) || !state.needsSync) return;
      setState({ needsSync: false }, { persist: true, changeType: "shopify-sync" });
    },
    subscribe: subscribe,
    openPreferences: function(trigger) {
      document.dispatchEvent(new CustomEvent("legitgrails:cookie-consent-open-preferences", {
        detail: { trigger: trigger || null }
      }));
    }
  });
  root.cookieConsent = consent;
  getState();
})(window);
